import { useEffect, useState } from 'react'
import { Bell, BellOff, LocateFixed, LocateOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toastHelpers } from '@/lib/toast-helpers'
import { lastSeenLabel } from '@/lib/riderLocationBuffer'
import { currentPushState, enableRiderPush, type PushState } from '@/lib/riderPush'
import type { RiderLocationSharing } from '@/hooks/useRiderLocationSharing'
import { nativeRequestRiderLocationPermission } from '@/lib/bhooklyNative'

// The rider's "is the restaurant seeing me?" line (F-DLV-N2): location sharing
// state with the fix for each way it can fail, and the alerts switch.

const LOCATION_HELP: Record<string, string> = {
  denied: 'Location is blocked. Allow location for this site in your browser settings, then reload.',
  unavailable: 'Can’t get a GPS fix. Turn on Location in your phone’s quick settings.',
  unsupported: 'This browser can’t share location. Use Chrome on your phone.',
}

export function RiderLiveStrip({
  onShift,
  location,
  now,
}: {
  onShift: boolean
  location: RiderLocationSharing
  now: number
}) {
  const [push, setPush] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    void currentPushState().then((s) => alive && setPush(s))
    return () => {
      alive = false
    }
  }, [])

  const turnOnAlerts = async () => {
    setBusy(true)
    try {
      const s = await enableRiderPush()
      setPush(s)
      if (s === 'on') toastHelpers.success('Alerts on', 'You’ll get a notification when a delivery is assigned to you.')
      else if (s === 'blocked') toastHelpers.error('Notifications are blocked', 'Allow notifications for this site in your browser settings.')
      else if (s === 'unsupported') toastHelpers.error('Alerts not available here', 'Install the app from Chrome (Add to Home screen) and try again.')
    } catch (err) {
      toastHelpers.error('Could not turn on alerts', (err as { message?: string })?.message || 'Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!onShift) return null
  const sharing = location.status === 'sharing'
  const native = location.mode === 'native'
  // In the Android app the fix for a refused permission is one tap away.
  const help =
    native && location.status === 'denied'
      ? 'Location is off for the Bhookly app. Tap “Allow location” below.'
      : LOCATION_HELP[location.status]
  const sentLabel = `sent ${lastSeenLabel(location.lastSentAt ?? location.lastFixAt, now)}`
  const sharingText = native
    ? location.background
      ? `Sharing location in the background · ${sentLabel}`
      : `Sharing location while the app is open · ${sentLabel}`
    : `Sharing location · ${sentLabel}${location.accuracyM != null ? ` · ±${location.accuracyM} m` : ''}`

  return (
    <div className="space-y-2">
      <div
        className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
          help ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200' : 'bg-card'
        }`}
      >
        <span className="inline-flex items-center gap-2">
          {sharing ? (
            <LocateFixed className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
          ) : (
            <LocateOff className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
          {help ?? (sharing ? sharingText : 'Finding your location…')}
        </span>
        {sharing && !native && !location.awake && (
          <span className="shrink-0 text-muted-foreground">Keep this screen open</span>
        )}
      </div>
      {native && (location.status === 'denied' || (sharing && !location.background)) && (
        <Button variant="outline" size="sm" className="w-full" onClick={nativeRequestRiderLocationPermission}>
          <LocateFixed className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {location.status === 'denied' ? 'Allow location' : 'Allow “all the time” so it works with the screen off'}
        </Button>
      )}
      {push === 'off' && (
        <Button variant="outline" size="sm" className="w-full" onClick={turnOnAlerts} disabled={busy}>
          <Bell className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Turn on alerts for new deliveries
        </Button>
      )}
      {push === 'blocked' && (
        <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <BellOff className="h-3.5 w-3.5" aria-hidden /> Alerts blocked — allow notifications for this site to get new deliveries.
        </p>
      )}
    </div>
  )
}
